import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Breadcrumbs,
  Chip,
  CircularProgress,
  Link as MuiLink,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import { Link } from 'react-router-dom';
import {
  AdminCategory,
  ApiError,
  ItemsWithoutCategoryRow,
  ItemTenant,
  getItemsWithoutCategory,
  listCategories,
  listItemTenants,
} from '../../api';
import { useAuth } from '../../auth/AuthContext';
import ItemEditDialog from '../dialogs/ItemEditDialog';
import Price from '../widgets/Price';

// Temporary cleanup report. Lists every non-deleted item whose
// `categoryId` is absent / null / empty. Clicking a row opens the
// existing edit dialog — same flow as ItemsSearchPage. Tear it out
// once the orphan list converges to zero.

const TENANT_LABELS: Record<string, string> = {
  backroom: 'Back Room',
  ags: 'Grow Shop',
};

const stockColor = (stock: number): string | undefined => {
  if (stock > 0) return undefined;
  return '#b91c1c';
};

const ItemsWithoutCategoryReport: React.FC = () => {
  const { idToken } = useAuth();

  const [rows, setRows] = useState<ItemsWithoutCategoryRow[] | null>(null);
  // Count of variants the report auto-fixed on the latest fetch by
  // inheriting their parent's category. Surfaced as a transient banner
  // so the operator can see what happened; reset to 0 on every
  // fetch so it never lingers across reloads.
  const [fixedVariants, setFixedVariants] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [tenants, setTenants] = useState<ItemTenant[] | null>(null);
  // Per-tenant category cache — populated on demand when a row is
  // picked. Same lazy pattern as ItemsSearchPage so the dialog's
  // Category dropdown is populated without paying upfront for every
  // tenant.
  const [catsByTenant, setCatsByTenant] = useState<
    Record<string, AdminCategory[]>
  >({});
  const [pickedRow, setPickedRow] = useState<ItemsWithoutCategoryRow | null>(
    null,
  );
  // Bump after a successful save to force a re-fetch — items that
  // gained a category should fall off the list immediately.
  const [reloadTick, setReloadTick] = useState(0);

  useEffect(() => {
    if (!idToken) return;
    let cancelled = false;
    listItemTenants(idToken)
      .then((rs) => {
        if (!cancelled) setTenants(rs);
      })
      .catch(() => {
        if (!cancelled) setTenants([]);
      });
    return () => {
      cancelled = true;
    };
  }, [idToken]);

  useEffect(() => {
    if (!idToken) return;
    let cancelled = false;
    setLoading(true);
    setError('');
    setFixedVariants(0);
    getItemsWithoutCategory(idToken)
      .then((r) => {
        if (!cancelled) {
          setRows(r.items);
          setFixedVariants(r.fixed_variants);
        }
      })
      .catch((e) => {
        if (cancelled) return;
        setError(
          e instanceof ApiError
            ? `API error ${e.status}`
            : (e as Error)?.message || 'fetch failed',
        );
        setRows(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [idToken, reloadTick]);

  // Lazy-load categories for the picked row's tenant. Mirrors the
  // ItemsSearchPage pattern — gates the dialog open state on the cache
  // being populated so the Category dropdown isn't blank on first open.
  useEffect(() => {
    if (!pickedRow || !idToken) return;
    const tenant = pickedRow.tenant;
    if (catsByTenant[tenant] !== undefined) return;
    let cancelled = false;
    listCategories(idToken, tenant)
      .then((cats) => {
        if (!cancelled) {
          setCatsByTenant((prev) => ({ ...prev, [tenant]: cats }));
        }
      })
      .catch(() => {
        if (!cancelled) {
          setCatsByTenant((prev) => ({ ...prev, [tenant]: [] }));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [pickedRow, idToken, catsByTenant]);

  const tenantEntry = useMemo(
    () =>
      pickedRow && tenants
        ? tenants.find((t) => t.slug === pickedRow.tenant)
        : undefined,
    [pickedRow, tenants],
  );
  const categoriesReady =
    pickedRow !== null && catsByTenant[pickedRow.tenant] !== undefined;

  return (
    <Stack spacing={3}>
      <Breadcrumbs aria-label="report breadcrumb">
        <MuiLink
          component={Link}
          to="/reports"
          underline="hover"
          sx={{ display: 'flex', alignItems: 'center', color: '#64748b' }}
        >
          <HomeIcon sx={{ mr: 0.5, fontSize: 18 }} />
          Reports
        </MuiLink>
        <Typography sx={{ color: '#1f2937', fontWeight: 500 }}>
          Items without category
        </Typography>
      </Breadcrumbs>

      <Box>
        <Typography variant="body2" sx={{ color: '#64748b' }}>
          Non-deleted items whose <code>categoryId</code> is missing,
          empty, or points to a category that no longer exists. Click a
          row to assign one. The list refreshes after each save.
          Variants whose parent has a valid category are auto-assigned
          on each refresh.
        </Typography>
      </Box>

      {fixedVariants > 0 && (
        <Alert severity="success">
          Auto-assigned parent's category to {fixedVariants} variant
          {fixedVariants === 1 ? '' : 's'}.
        </Alert>
      )}

      {error && <Alert severity="error">{error}</Alert>}

      {loading && rows === null && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={24} />
        </Box>
      )}

      {rows && (
        <Typography variant="body2" sx={{ color: '#64748b' }}>
          {rows.length} orphan
          {rows.length === 1 ? '' : 's'}
        </Typography>
      )}

      {rows && rows.length > 0 && (
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{ border: '1px solid #e5e7eb', borderRadius: 1 }}
        >
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#fafafa' }}>
                <TableCell sx={{ fontWeight: 600 }}>Tenant</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Reason</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Brand</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 600, textAlign: 'right' }}>
                  Price
                </TableCell>
                <TableCell sx={{ fontWeight: 600, textAlign: 'right' }}>
                  Stock
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Id</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow
                  key={`${row.tenant}-${row.id}`}
                  hover
                  onClick={() => setPickedRow(row)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell>
                    <Chip
                      label={TENANT_LABELS[row.tenant] ?? row.tenant}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap">
                      <Chip
                        label={
                          row.reason === 'missing'
                            ? 'no category'
                            : 'unknown category'
                        }
                        size="small"
                        color={row.reason === 'missing' ? 'default' : 'warning'}
                        variant={
                          row.reason === 'missing' ? 'outlined' : 'filled'
                        }
                        // Surface the dangling id as a tooltip for
                        // 'unknown' rows so the operator can spot a
                        // pattern (e.g. one deleted category producing
                        // dozens of orphans) without clicking through.
                        title={row.bad_category_id ?? undefined}
                      />
                      {row.parent_id && (
                        // Debug pill: this row passed the variant check
                        // (group.enabled + non-empty groupId +
                        // non-empty variantName). Useful for eyeballing
                        // whether the server's classification matches
                        // what the operator sees in Mongo.
                        <Chip
                          label="variant"
                          size="small"
                          color="info"
                          variant="filled"
                          title={`group.groupId = ${row.parent_id}`}
                        />
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell>{row.brand ?? '—'}</TableCell>
                  <TableCell>{row.name}</TableCell>
                  <TableCell sx={{ textAlign: 'right' }}>
                    <Price price={row.price ?? 0} showZero />
                  </TableCell>
                  <TableCell
                    sx={{
                      textAlign: 'right',
                      color: stockColor(row.stock),
                      fontWeight: row.stock > 0 ? 500 : undefined,
                    }}
                  >
                    {row.stock}
                  </TableCell>
                  <TableCell
                    sx={{
                      fontFamily: 'monospace',
                      fontSize: '0.75rem',
                      color: '#94a3b8',
                    }}
                  >
                    {row.id.slice(0, 8)}…
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {rows && rows.length === 0 && !loading && !error && (
        <Alert severity="success">
          No orphaned items — every non-deleted item has a category.
        </Alert>
      )}

      <ItemEditDialog
        open={pickedRow !== null && categoriesReady}
        tenant={pickedRow?.tenant ?? ''}
        imageRoot={tenantEntry?.image_root ?? ''}
        skuPrefix={tenantEntry?.sku_prefix ?? ''}
        itemId={pickedRow?.id ?? null}
        categories={
          pickedRow ? (catsByTenant[pickedRow.tenant] ?? []) : []
        }
        onClose={() => setPickedRow(null)}
        onSaved={() => setReloadTick((n) => n + 1)}
      />
    </Stack>
  );
};

export default ItemsWithoutCategoryReport;
